import * as THREE from 'three';
import { hash2, Voyage, type IslandSpec } from '@sea/world';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { buildIslandShape, type IslandAnchor, type IslandShape } from './islandShape';
import type { TreeGeometries } from './treeAssets';

/**
 * 岛屿：对象池 + 确定性布置。
 *
 * 造型思路（对比改前的「几个同心圆锥」）：
 *  - 地形是一张**极坐标高度场**，不平滑的地方全在形状里：不规则海岸线（谐波叠加）、
 *    偏离圆心的主峰 + 次峰、一条山脊线、域扭曲的脊状噪声、放射状冲沟，以及一条
 *    在 t≈0.9 处归零的岸线遮罩 + 独立的沙滩剖面（向水下继续延伸，让海浪和沙滩自然相交）。
 *  - 地表颜色**烘焙成顶点色**：按「高度 + 坡度」把沙 / 湿沙 / 草 / 枯草 / 岩连续地混起来，
 *    再用一条单独的雪壳盖住山顶（够高才显示）。不再是一层锥体一种颜色。
 *  - 树、礁石、灌木在构造时就从地形顶点里挑好了锚点（成簇、只长在缓坡草带），
 *    用 InstancedMesh 摆出来：一座岛 4 个 draw call，而不是几十个。
 *
 * 关键约束（§3.1）：
 *  1. 运行时**绝不新建几何体**：12 个槽位的地形、雪壳、树/石都在 constructor 里建好，
 *     `update()` 只改 position / rotation / scale / material / visible；
 *  2. 运行时**不加载任何外部资源**；
 *  3. 同一个 `IslandSpec` 永远长一样：形状由槽位种子决定，槽位由 `id % poolSize` 固定绑定，
 *     岛不会因为可见窗口滑动换了槽位而「变脸」。
 *
 * 预算：单岛地形 1680 三角形 + 雪壳 ≤ ~120 + 树 ≤ 22×28 + 礁石 ≤ 10×20 + 灌木 ≤ 8×20 ≈ 2.7k；
 * 12 座池子常驻内存，可见时每岛 5~6 个 draw call。
 */

/** 槽位形状种子：池子里第 i 个岛永远用 hash2(POOL_SEED, i) 这一套形状 */
const POOL_SEED = 0x5ea15e;
/** 雪壳显示阈值（米）：矮岛顶上放雪会很假 */
const SNOW_MIN_HEIGHT = 34;
/** 树 / 礁石 / 灌木的实例上限（与 islandShape 里的锚点上限一致） */
const MAX_TREES = 22;
const MAX_ROCKS = 10;
const MAX_SHRUBS = 8;

interface PaletteDef {
  readonly sand: number;
  readonly wet: number;
  readonly grass: number;
  readonly grassDark: number;
  readonly dry: number;
  readonly rock: number;
  readonly rockDark: number;
  readonly trunk: number;
  readonly canopy: number;
  readonly shrub: number;
}

/**
 * 4 种生物群系配色（style 0..1 → 选一个）。沙 / 草 / 岩各自有明暗两档，混色时用。
 *
 * 注意：场景的半球光偏蓝、强度高（会整体去饱和），所以沙色要刻意压暗、偏暖，
 * 否则浅色沙滩会在暖色阳光 + 冷色天光下直接过曝成一条白边。
 */
const PALETTE_DEFS: readonly PaletteDef[] = [
  // 热带：暖沙 + 饱和的绿 + 灰褐岩
  {
    sand: 0xd6b077,
    wet: 0x82683f,
    grass: 0x5c8b3c,
    grassDark: 0x38602a,
    dry: 0x938545,
    rock: 0x6f6656,
    rockDark: 0x4a453b,
    trunk: 0x54402f,
    canopy: 0x2f5a2c,
    shrub: 0x437a36,
  },
  // 冷湿：偏青的草 + 灰岩
  {
    sand: 0xc6bea4,
    wet: 0x777361,
    grass: 0x4a7150,
    grassDark: 0x2c4a35,
    dry: 0x807f58,
    rock: 0x606a68,
    rockDark: 0x414b4b,
    trunk: 0x4a3c30,
    canopy: 0x2c5240,
    shrub: 0x3b6750,
  },
  // 干暖：黄绿草坡 + 赭岩
  {
    sand: 0xd8bc82,
    wet: 0x866d42,
    grass: 0x74833e,
    grassDark: 0x4d5b2b,
    dry: 0xa08d48,
    rock: 0x796a50,
    rockDark: 0x514837,
    trunk: 0x56422e,
    canopy: 0x52702f,
    shrub: 0x617f38,
  },
  // 火山：灰沙 + 墨绿 + 深岩
  {
    sand: 0xbab3a2,
    wet: 0x706c60,
    grass: 0x4b6a5b,
    grassDark: 0x2a453e,
    dry: 0x6f7452,
    rock: 0x50555a,
    rockDark: 0x343a3f,
    trunk: 0x453930,
    canopy: 0x32563f,
    shrub: 0x3d6650,
  },
];

/** 雪：固定冷白，不随群系变（够高才有） */
const SNOW_LIGHT = new THREE.Color('#f4f8fa');
const SNOW_DARK = new THREE.Color('#c6d4dc');

interface Palette {
  readonly grass: THREE.Color;
  readonly grassDark: THREE.Color;
  readonly sand: THREE.Color;
  readonly wet: THREE.Color;
  readonly dry: THREE.Color;
  readonly rock: THREE.Color;
  readonly rockDark: THREE.Color;
}

const colorScratch = new THREE.Color();
const colorScratch2 = new THREE.Color();

/**
 * 顶点色烘焙：沙 / 草 / 岩按权重连续混合。
 * 分层感来自「同一张高度场上的权重渐变」，而不是几层不同颜色的几何体。
 */
function bakeTerrainColors(shape: IslandShape, palette: Palette): Float32Array {
  const count = shape.positions.length / 3;
  const out = new Float32Array(count * 3);
  const zones = shape.zones;
  for (let v = 0; v < count; v++) {
    const sandW = zones[v * 5] as number;
    const rockW = zones[v * 5 + 1] as number;
    const wetW = zones[v * 5 + 2] as number;
    const shade = zones[v * 5 + 3] as number;
    const dryW = zones[v * 5 + 4] as number;

    // 草：暗部 → 亮部，再掺一块块发黄的枯草
    const c = colorScratch.copy(palette.grassDark).lerp(palette.grass, shade);
    c.lerp(palette.dry, dryW * 0.7);
    // 沙
    c.lerp(palette.sand, sandW);
    // 岩：明暗也按同一个噪声走，免得岩石是一整块死色
    colorScratch2.copy(palette.rockDark).lerp(palette.rock, shade);
    c.lerp(colorScratch2, rockW);
    // 湿沙：只在贴近水面的地方，而且被岩石盖住的地方不刷
    c.lerp(palette.wet, wetW * (1 - rockW * 0.55));
    // 每顶点一点点亮度抖动：远看时地表不会是一整片均匀色
    const k = 0.93 + shade * 0.14;
    out[v * 3] = c.r * k;
    out[v * 3 + 1] = c.g * k;
    out[v * 3 + 2] = c.b * k;
  }
  return out;
}

/** 雪壳顶点色：冷白 + 少量明暗，避免一片纯白看起来像纸 */
function bakeSnowColors(shape: IslandShape): Float32Array {
  const count = shape.positions.length / 3;
  const out = new Float32Array(count * 3);
  const zones = shape.zones;
  const c = new THREE.Color();
  for (let v = 0; v < count; v++) {
    const shade = zones[v * 5 + 3] as number;
    c.copy(SNOW_DARK).lerp(SNOW_LIGHT, 0.35 + shade * 0.65);
    out[v * 3] = c.r;
    out[v * 3 + 1] = c.g;
    out[v * 3 + 2] = c.b;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * 共享几何体（构造期一次性建好，之后所有岛复用）
 * ------------------------------------------------------------------ */

/** 树干：5 边、去掉端盖（顶面永远在树冠里） */
const TRUNK_GEO = new THREE.CylinderGeometry(0.045, 0.07, 0.42, 5, 1, true);
TRUNK_GEO.translate(0, 0.21, 0);

/**
 * 树冠：两级锥体合并成一个几何体（下宽上窄），低多边形但读得出「树」的形状。
 * 树冠底边从 0.24 开始，所以树干至少露出 24% 的高度。
 */
function makeCanopyGeometry(): THREE.BufferGeometry {
  const lower = new THREE.ConeGeometry(0.4, 0.56, 6, 1, false);
  lower.translate(0, 0.24 + 0.28, 0);
  const upper = new THREE.ConeGeometry(0.27, 0.46, 6, 1, true);
  upper.translate(0, 0.54 + 0.23, 0);
  return mergeGeometries([lower, upper], false) ?? lower;
}
const CANOPY_GEO = makeCanopyGeometry();

/**
 * 阔叶树冠：一个低面数的球，坐在树干顶上。
 * 和针叶树混着放，一片林子里就不会全是同一个「圣诞树」形状。
 */
const BROADLEAF_GEO = new THREE.IcosahedronGeometry(0.42, 0);
BROADLEAF_GEO.scale(1, 0.88, 1);
BROADLEAF_GEO.translate(0, 0.72, 0);

/** 礁石 / 灌木：20 个面的二十面体，靠非均匀缩放变成各种石头 */
const ROCK_GEO = new THREE.IcosahedronGeometry(0.5, 0);
const SHRUB_GEO = new THREE.IcosahedronGeometry(0.5, 0);

const matrixScratch = new THREE.Matrix4();
const quatScratch = new THREE.Quaternion();
const eulerScratch = new THREE.Euler();
const positionScratch = new THREE.Vector3();
const scaleScratch = new THREE.Vector3();

interface Slot {
  readonly group: THREE.Group;
  /** 4 个群系各一份顶点色（几何体共享位置/法线，只有颜色不同） */
  readonly terrain: readonly THREE.Mesh[];
  readonly snow: THREE.Mesh | null;
  readonly trunks: THREE.InstancedMesh;
  readonly canopies: THREE.InstancedMesh;
  /** 阔叶树冠（和针叶树分开画，几何体不同） */
  readonly broadleaves: THREE.InstancedMesh;
  readonly rocks: THREE.InstancedMesh;
  readonly shrubs: THREE.InstancedMesh;
  readonly treeAnchors: readonly IslandAnchor[];
  readonly rockAnchors: readonly IslandAnchor[];
  readonly shrubAnchors: readonly IslandAnchor[];
  assigned: IslandSpec | null;
  /** 本帧的占用标记（配合 frameStamp 做槽位分配） */
  stamp: number;
}

export class IslandField {
  readonly group = new THREE.Group();
  private readonly slots: Slot[] = [];
  /** 启动时由 main 注入的 GLB 树几何体（null = 用程序化锥体树） */
  private treeGeometries: TreeGeometries | null = null;
  private readonly treeMaterial = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  private readonly palettes: readonly Palette[];
  private readonly materials: {
    terrain: THREE.MeshLambertMaterial;
    snow: THREE.MeshLambertMaterial;
    trunk: THREE.MeshLambertMaterial[];
    canopy: THREE.MeshLambertMaterial[];
    rock: THREE.MeshLambertMaterial[];
    shrub: THREE.MeshLambertMaterial[];
  };
  private frameStamp = 0;

  constructor(poolSize = 12) {
    this.materials = {
      // 地形：顶点色自己带颜色，材质保持纯白做乘法。polygonOffset 把地形往后推一点点，
      // 水线附近就由海面赢下深度测试，不会出现闪烁的交界线。
      terrain: new THREE.MeshLambertMaterial({
        vertexColors: true,
        // 低多边形：面级硬边把「山脊 / 冲沟」的转折读出来，比全平滑更像地形
        flatShading: true,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      }),
      // 雪壳盖在地形表面：负的 polygonOffset 让它稳定地赢过地形，不会 z-fighting
      snow: new THREE.MeshLambertMaterial({
        vertexColors: true,
        flatShading: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
      trunk: PALETTE_DEFS.map(
        (p) => new THREE.MeshLambertMaterial({ color: p.trunk, flatShading: true }),
      ),
      canopy: PALETTE_DEFS.map(
        (p) => new THREE.MeshLambertMaterial({ color: p.canopy, flatShading: true }),
      ),
      rock: PALETTE_DEFS.map(
        (p) => new THREE.MeshLambertMaterial({ color: p.rock, flatShading: true }),
      ),
      shrub: PALETTE_DEFS.map(
        (p) => new THREE.MeshLambertMaterial({ color: p.shrub, flatShading: true }),
      ),
    };

    this.palettes = PALETTE_DEFS.map((p) => ({
      grass: new THREE.Color(p.grass),
      grassDark: new THREE.Color(p.grassDark),
      sand: new THREE.Color(p.sand),
      wet: new THREE.Color(p.wet),
      dry: new THREE.Color(p.dry),
      rock: new THREE.Color(p.rock),
      rockDark: new THREE.Color(p.rockDark),
    }));

    for (let i = 0; i < poolSize; i++) {
      this.slots.push(this.createSlot(i));
    }
  }

  private createSlot(index: number): Slot {
    const group = new THREE.Group();
    group.visible = false;

    // 每个槽位一套独立的形状（同一个 seed 永远长一样）
    const shape = buildIslandShape(hash2(POOL_SEED, index));

    // 顶点位置 / 法线 / 索引在 4 个群系之间共享，只有 color 不同
    const positionAttr = new THREE.BufferAttribute(shape.positions, 3);
    const normalAttr = new THREE.BufferAttribute(shape.normals, 3);
    const indexAttr = new THREE.BufferAttribute(shape.index, 1);

    const terrain: THREE.Mesh[] = [];
    for (let p = 0; p < this.palettes.length; p++) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', positionAttr);
      geo.setAttribute('normal', normalAttr);
      geo.setAttribute('color', new THREE.BufferAttribute(bakeTerrainColors(shape, this.palettes[p]!), 3));
      geo.setIndex(indexAttr);
      geo.computeBoundingSphere();

      const mesh = new THREE.Mesh(geo, this.materials.terrain);
      mesh.visible = p === 0;
      group.add(mesh);
      terrain.push(mesh);
    }

    // 雪壳：共享地形的顶点，只保留雪线以上的三角形
    let snow: THREE.Mesh | null = null;
    if (shape.snowIndex.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', positionAttr);
      geo.setAttribute('normal', normalAttr);
      geo.setAttribute('color', new THREE.BufferAttribute(bakeSnowColors(shape), 3));
      geo.setIndex(new THREE.BufferAttribute(shape.snowIndex, 1));
      geo.computeBoundingSphere();
      snow = new THREE.Mesh(geo, this.materials.snow);
      snow.visible = false;
      group.add(snow);
    }

    // 树 / 礁石 / 灌木：每座岛每种一个 InstancedMesh → 4 个 draw call
    const trunks = new THREE.InstancedMesh(TRUNK_GEO, this.materials.trunk[0]!, MAX_TREES);
    trunks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    trunks.frustumCulled = false;
    trunks.count = 0;
    group.add(trunks);

    const canopies = new THREE.InstancedMesh(CANOPY_GEO, this.materials.canopy[0]!, MAX_TREES);
    canopies.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    canopies.frustumCulled = false;
    canopies.count = 0;
    group.add(canopies);

    const broadleaves = new THREE.InstancedMesh(BROADLEAF_GEO, this.materials.canopy[0]!, MAX_TREES);
    broadleaves.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    broadleaves.frustumCulled = false;
    broadleaves.count = 0;
    group.add(broadleaves);

    const rocks = new THREE.InstancedMesh(ROCK_GEO, this.materials.rock[0]!, MAX_ROCKS);
    rocks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    rocks.frustumCulled = false;
    rocks.count = 0;
    group.add(rocks);

    const shrubs = new THREE.InstancedMesh(SHRUB_GEO, this.materials.shrub[0]!, MAX_SHRUBS);
    shrubs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    shrubs.frustumCulled = false;
    shrubs.count = 0;
    group.add(shrubs);

    this.group.add(group);
    return {
      group,
      terrain,
      snow,
      trunks,
      canopies,
      broadleaves,
      rocks,
      shrubs,
      treeAnchors: shape.trees,
      rockAnchors: shape.rocks,
      shrubAnchors: shape.shrubs,
      assigned: null,
      stamp: 0,
    };
  }

  /**
   * 注入 GLB 树资产（启动时加载一次）。
   * 注入后：树冠/阔叶的 InstancedMesh 换成 GLB 几何体，
   * 树干 InstancedMesh 归零（GLB 自带树干）。
   */
  setTreeGeometries(geometries: TreeGeometries | null): void {
    this.treeGeometries = geometries;
    if (!geometries) return;
    for (const slot of this.slots) {
      slot.canopies.geometry = geometries.conifer;
      slot.broadleaves.geometry = geometries.broadleaf;
      slot.trunks.count = 0;
      slot.canopies.material = this.treeMaterial;
      slot.broadleaves.material = this.treeMaterial;
    }
  }

  update(voyage: Voyage, distance: number, back = 70, ahead = 440): number {
    voyage.ensure(distance + ahead);

    const visible = voyage.visible(distance, back, ahead);
    // 槽位按 id 固定绑定：同一座岛不会因为可见窗口滑动换到别的槽位、变一副样子。
    // 万一同时可见的岛比池子还多（改大了可见范围才可能发生），才退化成「找个空槽」。
    const stamp = ++this.frameStamp;
    const slots = this.slots;
    let shown = 0;
    for (const spec of visible) {
      if (shown >= slots.length) break;
      let slot: Slot | undefined = slots[spec.id % slots.length];
      if (!slot || slot.stamp === stamp) {
        slot = slots.find((s) => s.stamp !== stamp);
      }
      if (!slot) break;
      slot.stamp = stamp;
      this.applySpec(slot, spec, distance);
      shown++;
    }

    for (const slot of slots) {
      if (slot.stamp !== stamp) {
        slot.group.visible = false;
        slot.assigned = null;
      }
    }
    return shown;
  }

  private applySpec(slot: Slot, spec: IslandSpec, distance: number): void {
    const z = Voyage.zAt(spec, distance);

    // 位置每帧都要跟着航程走，所以放在缓存判断之前
    slot.group.visible = true;
    slot.group.position.set(spec.lateral, 0, z);
    if (slot.assigned === spec) return;
    slot.assigned = spec;

    const paletteIndex = Math.min(
      PALETTE_DEFS.length - 1,
      Math.max(0, Math.floor(spec.style * PALETTE_DEFS.length)),
    );
    const radius = Math.max(1, spec.radius);
    const height = Math.max(0.5, spec.height);

    slot.group.rotation.y = spec.rotation;

    // 群系：4 份顶点色里只显示一份（其余 visible=false，不产生 draw call）
    for (let i = 0; i < slot.terrain.length; i++) {
      const mesh = slot.terrain[i]!;
      mesh.scale.set(radius, height, radius);
      mesh.visible = i === paletteIndex;
    }
    if (slot.snow) {
      slot.snow.scale.set(radius, height, radius);
      slot.snow.visible = height >= SNOW_MIN_HEIGHT;
    }

    /* ---------------- 树 ---------------- */
    const treeHeight = Math.min(11, Math.max(2.5, radius * 0.06));
    const treeCount = Math.min(
      slot.treeAnchors.length,
      MAX_TREES,
      Math.round(12 + spec.feature * 8 + spec.style * 2),
    );
    let coniferCount = 0;
    let broadleafCount = 0;
    for (let i = 0; i < treeCount; i++) {
      const a = slot.treeAnchors[i]!;
      const s = treeHeight * a.scale;
      // 树干底埋进地面一点点，坡地上不会露出缝隙
      positionScratch.set(a.x * radius, a.y * height - s * 0.04, a.z * radius);
      eulerScratch.set(0, a.rot, 0);
      quatScratch.setFromEuler(eulerScratch);
      // 竖直方向按 stretch 拉长/压扁：同一套几何体也有高矮差别
      scaleScratch.set(s, s * Math.min(1.25, a.stretch), s);
      matrixScratch.compose(positionScratch, quatScratch, scaleScratch);
      // 没有 GLB 树时才用单独的程序化树干（GLB 自带树干）
      if (!this.treeGeometries) slot.trunks.setMatrixAt(i, matrixScratch);
      // 每 4 棵里换 1 棵阔叶树，避免整片林子都是同一种剪影
      if (i % 4 === 2) slot.broadleaves.setMatrixAt(broadleafCount++, matrixScratch);
      else slot.canopies.setMatrixAt(coniferCount++, matrixScratch);
    }
    slot.trunks.count = this.treeGeometries ? 0 : treeCount;
    slot.canopies.count = coniferCount;
    slot.broadleaves.count = broadleafCount;
    slot.trunks.instanceMatrix.needsUpdate = true;
    slot.canopies.instanceMatrix.needsUpdate = true;
    slot.broadleaves.instanceMatrix.needsUpdate = true;
    if (!this.treeGeometries) {
      slot.trunks.material = this.materials.trunk[paletteIndex]!;
      slot.canopies.material = this.materials.canopy[paletteIndex]!;
      slot.broadleaves.material = this.materials.canopy[paletteIndex]!;
    } else {
      // GLB 自带树皮/树冠颜色（烘焙进顶点色），这里只按岛的纬度换棕榈
      slot.broadleaves.geometry =
        height < 26 ? this.treeGeometries.palm : this.treeGeometries.broadleaf;
    }

    /* ---------------- 礁石 ---------------- */
    const rockUnit = Math.max(0.5, radius * 0.05);
    const rockCount = Math.min(
      slot.rockAnchors.length,
      MAX_ROCKS,
      Math.round(4 + spec.feature * 8),
    );
    for (let i = 0; i < rockCount; i++) {
      const a = slot.rockAnchors[i]!;
      const s = rockUnit * a.scale;
      // 抬起来一点：岸边礁石要半沉半露
      positionScratch.set(a.x * radius, a.y * height + s * 0.12, a.z * radius);
      eulerScratch.set(a.rot * 0.7, a.rot, a.rot * 1.3);
      quatScratch.setFromEuler(eulerScratch);
      scaleScratch.set(s * 1.25, s * 0.85, s);
      matrixScratch.compose(positionScratch, quatScratch, scaleScratch);
      slot.rocks.setMatrixAt(i, matrixScratch);
    }
    slot.rocks.count = rockCount;
    slot.rocks.instanceMatrix.needsUpdate = true;
    slot.rocks.material = this.materials.rock[paletteIndex]!;

    /* ---------------- 灌木 ---------------- */
    const shrubUnit = Math.max(0.5, radius * 0.018);
    const shrubCount = Math.min(
      slot.shrubAnchors.length,
      MAX_SHRUBS,
      Math.round(2 + (1 - spec.feature) * 5 + spec.style * 2),
    );
    for (let i = 0; i < shrubCount; i++) {
      const a = slot.shrubAnchors[i]!;
      const s = shrubUnit * a.scale;
      positionScratch.set(a.x * radius, a.y * height + s * 0.25, a.z * radius);
      eulerScratch.set(0, a.rot, 0);
      quatScratch.setFromEuler(eulerScratch);
      scaleScratch.set(s * 1.15, s * 0.9, s);
      matrixScratch.compose(positionScratch, quatScratch, scaleScratch);
      slot.shrubs.setMatrixAt(i, matrixScratch);
    }
    slot.shrubs.count = shrubCount;
    slot.shrubs.instanceMatrix.needsUpdate = true;
    slot.shrubs.material = this.materials.shrub[paletteIndex]!;
  }

  get activeCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.group.visible) n++;
    return n;
  }
}
